import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from '../design-system';
import { theme } from '../design-system/theme';
import { PlatformPolicy, PlatformConsentStatus } from '../services/consentApi';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(33, 36, 53, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Header = styled.div`
  padding: 24px 24px 0;
  text-align: center;
`;

const IconWrapper = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  background: ${theme.infoLight};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${theme.textPrimary};
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${theme.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const Content = styled.div`
  padding: 24px;
`;

const VersionInfo = styled.div`
  background: ${theme.surfaceMuted};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
`;

const VersionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${theme.border};

  &:last-child {
    border-bottom: none;
  }
`;

const VersionLabel = styled.span`
  font-size: 14px;
  color: ${theme.textSecondary};
`;

const VersionValue = styled.span<{ $isNew?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$isNew ? theme.info : theme.textPrimary};
`;

const PolicySection = styled.div`
  margin-bottom: 20px;
`;

const PolicyTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${theme.textPrimary};
  margin: 0 0 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PolicyContent = styled.div`
  background: ${theme.surfaceMuted};
  border: 1px solid ${theme.border};
  border-radius: 8px;
  padding: 16px;
  max-height: 200px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.6;
  color: ${theme.textSecondary};

  h4 {
    font-size: 14px;
    color: ${theme.textPrimary};
    margin: 16px 0 8px;
    
    &:first-child {
      margin-top: 0;
    }
  }

  ul, ol {
    margin: 8px 0;
    padding-left: 20px;
  }

  li {
    margin: 4px 0;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 20px 0;
  padding: 16px;
  background: ${theme.surfaceMuted};
  border-radius: 8px;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;

  input[type="checkbox"] {
    margin-top: 2px;
  }
`;

const CheckboxLabel = styled.span`
  font-size: 14px;
  color: ${theme.textPrimary};
  line-height: 1.4;

  a {
    color: ${theme.info};
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.div`
  background: ${theme.dangerLight};
  border: 1px solid ${theme.danger};
  border-radius: 8px;
  padding: 12px 16px;
  color: ${theme.danger};
  font-size: 14px;
  margin-bottom: 16px;
`;

const Footer = styled.div`
  padding: 16px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const LogoutLink = styled.button`
  background: none;
  border: none;
  color: ${theme.textSecondary};
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;

  &:hover {
    color: ${theme.textPrimary};
  }
`;

interface ReconsentPopupProps {
  policies: PlatformPolicy[];
  consentStatus: PlatformConsentStatus | null;
  isLoading: boolean;
  error: string | null;
  onAccept: (privacyAccepted: boolean, termsAccepted: boolean) => Promise<void>;
  onLogout: () => void;
}

/**
 * Popup displayed when a user needs to re-consent to updated platform policies.
 * 
 * This component should be rendered as a modal overlay when the user's
 * previously accepted policy versions are outdated.
 */
export const ReconsentPopup: React.FC<ReconsentPopupProps> = ({
  policies,
  consentStatus,
  isLoading,
  error,
  onAccept,
  onLogout,
}) => {
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const privacyPolicy = policies.find(p => p.policyType === 'PRIVACY_POLICY');
  const termsOfService = policies.find(p => p.policyType === 'TERMS_OF_SERVICE');

  const canSubmit = privacyAccepted && termsAccepted && !isLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onAccept(privacyAccepted, termsAccepted);
  };

  // Determine which policies have changed
  const privacyPolicyChanged = consentStatus && 
    consentStatus.privacyPolicyVersion !== consentStatus.currentPrivacyPolicyVersion;
  const termsOfServiceChanged = consentStatus && 
    consentStatus.termsOfServiceVersion !== consentStatus.currentTermsOfServiceVersion;

  return (
    <Overlay>
      <Modal role="dialog" aria-labelledby="reconsent-title" aria-modal="true">
        <Header>
          <IconWrapper>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={theme.info} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </IconWrapper>
          <Title id="reconsent-title">Policy Update</Title>
          <Subtitle>
            We've updated our policies to better protect your data and rights.
            Please review and accept the updated terms to continue.
          </Subtitle>
        </Header>

        <Content>
          {consentStatus && (
            <VersionInfo>
              <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, color: theme.textPrimary }}>
                What's Changed
              </h4>
              {privacyPolicyChanged && (
                <VersionRow>
                  <VersionLabel>Privacy Policy</VersionLabel>
                  <div>
                    <VersionValue>{consentStatus.privacyPolicyVersion || 'Not accepted'}</VersionValue>
                    <span style={{ margin: '0 8px', color: theme.textSubtle }}>→</span>
                    <VersionValue $isNew>{consentStatus.currentPrivacyPolicyVersion}</VersionValue>
                  </div>
                </VersionRow>
              )}
              {termsOfServiceChanged && (
                <VersionRow>
                  <VersionLabel>Terms of Service</VersionLabel>
                  <div>
                    <VersionValue>{consentStatus.termsOfServiceVersion || 'Not accepted'}</VersionValue>
                    <span style={{ margin: '0 8px', color: theme.textSubtle }}>→</span>
                    <VersionValue $isNew>{consentStatus.currentTermsOfServiceVersion}</VersionValue>
                  </div>
                </VersionRow>
              )}
            </VersionInfo>
          )}

          {privacyPolicy && (
            <PolicySection>
              <PolicyTitle>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Privacy Policy
                {privacyPolicyChanged && (
                  <span style={{ 
                    fontSize: '11px', 
                    background: theme.info, 
                    color: 'white', 
                    padding: '2px 6px', 
                    borderRadius: '4px',
                    fontWeight: 500
                  }}>
                    Updated
                  </span>
                )}
              </PolicyTitle>
              <PolicyContent dangerouslySetInnerHTML={{ __html: privacyPolicy.content }} />
            </PolicySection>
          )}

          {termsOfService && (
            <PolicySection>
              <PolicyTitle>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
                Terms of Service
                {termsOfServiceChanged && (
                  <span style={{ 
                    fontSize: '11px', 
                    background: theme.info, 
                    color: 'white', 
                    padding: '2px 6px', 
                    borderRadius: '4px',
                    fontWeight: 500
                  }}>
                    Updated
                  </span>
                )}
              </PolicyTitle>
              <PolicyContent dangerouslySetInnerHTML={{ __html: termsOfService.content }} />
            </PolicySection>
          )}

          <CheckboxGroup>
            <CheckboxItem>
              <input
                type="checkbox"
                checked={privacyAccepted}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrivacyAccepted(e.target.checked)}
                disabled={isLoading}
              />
              <CheckboxLabel>
                I have read and agree to the <strong>Privacy Policy</strong>
              </CheckboxLabel>
            </CheckboxItem>
            <CheckboxItem>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTermsAccepted(e.target.checked)}
                disabled={isLoading}
              />
              <CheckboxLabel>
                I have read and agree to the <strong>Terms of Service</strong>
              </CheckboxLabel>
            </CheckboxItem>
          </CheckboxGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Content>

        <Footer>
          <ButtonGroup>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
              isLoading={isLoading}
              style={{ flex: 1 }}
            >
              Accept &amp; Continue
            </Button>
          </ButtonGroup>
          <div style={{ textAlign: 'center' }}>
            <LogoutLink onClick={onLogout} disabled={isLoading}>
              I don't agree, log me out
            </LogoutLink>
          </div>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default ReconsentPopup;
